import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_LOCALE, LOCALES, isLocale, translator, type Locale } from '@st/i18n';
import { portalDict } from './dict';

export type TFunction = (key: string, vars?: Record<string, string | number>) => string;

interface I18nValue {
  locale: Locale;
  locales: readonly Locale[];
  setLocale: (l: Locale) => void;
  t: TFunction;
}

const I18nContext = createContext<I18nValue | null>(null);

function interpolate(value: string, vars?: Record<string, string | number>): string {
  if (!vars) return value;
  return value.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`));
}

function lookup(source: unknown, key: string): string | undefined {
  if (typeof key !== 'string' || key === '') return undefined;
  let cur: unknown = source;
  for (const part of key.split('.')) {
    if (typeof cur !== 'object' || cur === null || !(part in cur)) return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return typeof cur === 'string' ? cur : undefined;
}

/**
 * `@st/i18n` je izvor istine; `src/i18n/dict.ts` samo dopunjuje ključeve
 * koje treba isključivo portal. Redoslijed pretrage to i odražava.
 */
export function makeT(locale: Locale): TFunction {
  const base = translator(locale);
  const extra = portalDict[locale];
  return (key, vars) => {
    const fromBase = base(key, vars);
    if (fromBase !== key) return fromBase;
    const fromPortal = lookup(extra, key);
    return fromPortal === undefined ? key : interpolate(fromPortal, vars);
  };
}

export function I18nProvider({
  children,
  locale: controlled,
  onLocaleChange,
}: {
  children: ReactNode;
  /** Jezik iz korisnikovog profila. Ako nije zadan, portal koristi hrvatski. */
  locale?: Locale | undefined;
  onLocaleChange?: (l: Locale) => void;
}) {
  const [fallback, setFallback] = useState<Locale>(DEFAULT_LOCALE);
  const locale = controlled ?? fallback;

  const setLocale = useCallback(
    (l: Locale) => {
      setFallback(l);
      onLocaleChange?.(l);
    },
    [onLocaleChange],
  );

  const value = useMemo<I18nValue>(
    () => ({ locale, locales: LOCALES, setLocale, t: makeT(locale) }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation mora biti unutar <I18nProvider>');
  return ctx;
}

export { isLocale };
